/* eslint-disable prettier/prettier */
import { useTranslation } from 'react-i18next';
import { MDRegular, SMBold } from '@/typography/Typography';
import { SearchGrid } from '@/components/SearchGrid';
import {
    Box,
    Divider,
    Stack,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableRow
} from '@/components/shared/styles/Table';
import CardHeader from '@/components/shared/CardHeader';
import { TableCard } from '@/components/shared/Card';
import { MonthPicker } from '@/components/shared/MonthPicker';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { DATE_FORMAT } from '@/lib/constants';
import { normalizeExcelCellValue } from '@/utils/excelExport';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, usePayments } from '@/lib/hooks';
import { getReconciliationPdf } from '@/lib/slices/paymentsSlice';
import { useFormatting } from '@/utils/formatting';
import { UserContext } from '@/contexts/user/UserContext';

import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import { TipsReconciliationTable } from '@/pages/tips/TipsReconciliationTable';
import { useTipsReconciliationReport } from '@/services/paymentsQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';

export const TipsReconciliationPage = () => {
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState<Dayjs>(
        dayjs().month(dayjs().month() - 1)
    );

    const {
        data: tipsReconciliation,
        error,
        isPending
    } = useTipsReconciliationReport(
        selectedDate.format('MM'),
        selectedDate.format('YYYY')
    );

    useHandleResponse({ error, isPending });

    const { formatDate, getBalanceLabel, formatDateTime } = useFormatting();

    const dateHeader = useMemo(() => {
        if (selectedDate) {
            return selectedDate.format(DATE_FORMAT.MONTH_YEAR);
        }
    }, [selectedDate]);

    const monthName = useMemo(() => {
        if (selectedDate) {
            return selectedDate.format(DATE_FORMAT.MONTH_NAME);
        }
    }, [selectedDate]);

    const yearName = useMemo(() => {
        if (selectedDate) {
            return selectedDate.format(DATE_FORMAT.YEAR);
        }
    }, [selectedDate]);

    const dispatch = useAppDispatch();
    const [pdfDownloadReady, setPdfDownloadReady] = useState(false);

    const { company } = useContext(UserContext);

    // Helper function to format payroll period
    const formatPayrollPeriod = (start: string, end: string) => {
        return (
            formatDate(start, DATE_FORMAT.DAY_MONTH, true) +
            ' - ' +
            formatDate(end, DATE_FORMAT.DAY_MONTH, true)
        );
    };

    // Calculate total for an array of summaries
    const calculateTotal = (summaries: any[] | undefined) => {
        if (!summaries || !Array.isArray(summaries)) return 0;
        return summaries.reduce(
            (acc, summary) => acc + (summary.total || 0),
            0
        );
    };

    const onPressDownload = () => {
        const sheet = XLSXUtils.aoa_to_sheet([]);
        const sheetData: any[] = [[monthName, yearName], ['']];
        const boldCells: string[] = [];
        let currentRow = 3;

        const header = [
            t('employeeType'),
            t('date'),
            t('distributedAt'),
            t('reportedAt'),
            t('description'),
            t('amount'),
            t('sum')
        ];

        // Process hourly employees - now an array of periods
        const hourlyReconciliations =
            tipsReconciliation?.data?.hourlyEmployeesTipsReconciliation;

        if (hourlyReconciliations) {
            hourlyReconciliations.forEach((period: any) => {
                const periodText = formatPayrollPeriod(
                    period.payrollPeriodStart,
                    period.payrollPeriodEnd
                );
                const periodEmployees: any[] = [];

                period.tipsTransactions?.forEach((transaction: any) => {
                    periodEmployees.push([
                        t('hourly_employees'),
                        normalizeExcelCellValue(
                            transaction.date,
                            company?.ianaTimeZone
                        ),
                        normalizeExcelCellValue(
                            transaction.createdAt,
                            company?.ianaTimeZone
                        ),
                        normalizeExcelCellValue(
                            transaction.reportedAt,
                            company?.ianaTimeZone
                        ),
                        transaction.description,
                        transaction.amount,
                        transaction.sum
                    ]);
                });

                sheetData.push([t('hourly_employees'), periodText]);
                currentRow++;

                sheetData.push(header);
                boldCells.push(
                    `A${currentRow}`,
                    `B${currentRow}`,
                    `C${currentRow}`,
                    `D${currentRow}`,
                    `E${currentRow}`,
                    `F${currentRow}`
                );
                currentRow++;

                sheetData.push(...periodEmployees);
                currentRow += periodEmployees.length;

                sheetData.push([t('total'), '', '', '', period.total, '']);
                boldCells.push(`A${currentRow}`, `E${currentRow}`);
                currentRow++;

                sheetData.push(['']);
                currentRow++;
            });
        }

        // Process fulltime employees - now an array of periods
        const fulltimeReconciliations =
            tipsReconciliation?.data?.fullTimeEmployeesTipsReconciliation;

        if (fulltimeReconciliations) {
            fulltimeReconciliations.forEach((period: any) => {
                const periodText = formatPayrollPeriod(
                    period.payrollPeriodStart,
                    period.payrollPeriodEnd
                );
                const periodEmployees: any[] = [];

                period.tipsTransactions?.forEach((transaction: any) => {
                    periodEmployees.push([
                        t('fulltime_employees'),
                        normalizeExcelCellValue(
                            transaction.date,
                            company?.ianaTimeZone
                        ),
                        normalizeExcelCellValue(
                            transaction.createdAt,
                            company?.ianaTimeZone
                        ),
                        normalizeExcelCellValue(
                            transaction.reportedAt,
                            company?.ianaTimeZone
                        ),
                        transaction.description,
                        transaction.amount,
                        transaction.sum
                    ]);
                });

                sheetData.push([t('fulltime_employees'), periodText]);
                currentRow++;

                sheetData.push(header);
                boldCells.push(
                    `A${currentRow}`,
                    `B${currentRow}`,
                    `C${currentRow}`,
                    `D${currentRow}`,
                    `E${currentRow}`,
                    `F${currentRow}`
                );
                currentRow++;

                sheetData.push(...periodEmployees);
                currentRow += periodEmployees.length;

                sheetData.push([t('total'), '', '', '', period.total, '']);
                boldCells.push(`A${currentRow}`, `E${currentRow}`);
                currentRow++;

                sheetData.push(['']);
                currentRow++;
            });
        }

        // Add month total
        sheetData.push([
            t('total_for_month', { month: monthName }),
            '',
            '',
            '',
            tipsReconciliation?.data?.tipsReconciliationSummary?.monthTotal,
            ''
        ]);
        boldCells.push(`A${currentRow}`, `E${currentRow}`);

        XLSXUtils.sheet_add_aoa(sheet, sheetData);

        const workbook = XLSXUtils.book_new();
        const boldStyle = { font: { bold: true } };

        boldCells.forEach((cell) => {
            if (sheet[cell]) sheet[cell].s = boldStyle;
        });

        const wscols = [
            { wch: 20 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 30 },
            { wch: 20 }
        ];
        sheet['!cols'] = wscols;

        XLSXUtils.book_append_sheet(workbook, sheet, 'Report');

        XLSXWriteFile(
            workbook,
            `Tips Reconciliation report ${formatDate(
                selectedDate,
                DATE_FORMAT.MONTH_YEAR
            )}.xlsx`
        );
    };

    const handleDownloadPdf = async () => {
        try {
            let response =
                await PaymentsAPIServiceV2.getInstance().getTipsReconciliationReportPDF(
                    selectedDate.format('MM'),
                    selectedDate.format('YYYY')
                );

            const anchor = document.createElement('a');
            anchor.href = response?.data?.fileUrl;
            anchor.click();
            anchor.remove();
        } catch (error) {
            console.error('Error downloading reconciliation:', error);
        }
    };

    return (
        <div>
            <MDRegular sx={{ mb: 3, color: '#667085' }}>
                {t('tips.tips_reconciliation_subtitle')}
            </MDRegular>
            <SearchGrid container justifyContent={'space-between'}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={1}
                    justifyContent={'space-between'}
                >
                    <div>
                        <SMBold noWrap={true}>{t('select_month')}</SMBold>
                    </div>

                    <MonthPicker
                        label={'Select month'}
                        value={selectedDate.toDate()}
                        onChange={setSelectedDate}
                    />
                </Stack>
            </SearchGrid>

            <Box m={4}></Box>

            <TableCard>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    mr={2}
                >
                    <CardHeader
                        label={t('total_for_month', { month: dateHeader })}
                    ></CardHeader>
                    <span style={{ display: 'flex', gap: '8px' }}>
                        <SecondaryButtonRegular
                            onClick={onPressDownload}
                            size="small"
                            sx={{ borderRadius: '5px' }}
                        >
                            {t('download_as_excel')}
                        </SecondaryButtonRegular>
                        <PrimaryButton onClick={handleDownloadPdf} size="small">
                            {t('download_as_pdf')}
                        </PrimaryButton>
                    </span>
                </Stack>
                <Divider style={{ width: '100%' }} />
                <TableContainer>
                    <Table>
                        <TableBody>
                            {/* Hourly employees - multiple periods */}
                            {tipsReconciliation?.data?.tipsReconciliationSummary?.hourlyEmployeesTipsSummary?.map(
                                (period, index) => (
                                    <TableRow key={`hourly-${index}`}>
                                        <TableCell width={'390px'}>
                                            {formatPayrollPeriod(
                                                period.payrollPeriodStart,
                                                period.payrollPeriodEnd
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t('total_hourly_tips')}
                                        </TableCell>
                                        <TableCell align="right">
                                            {getBalanceLabel(
                                                period.total,
                                                company.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            )}

                            {/* Fulltime employees - multiple periods */}
                            {tipsReconciliation?.data?.tipsReconciliationSummary?.fullTimeEmployeesTipsSummary?.map(
                                (period, index) => (
                                    <TableRow key={`fulltime-${index}`}>
                                        <TableCell>
                                            {formatPayrollPeriod(
                                                period.payrollPeriodStart,
                                                period.payrollPeriodEnd
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t('total_fulltime_tips')}
                                        </TableCell>
                                        <TableCell align="right">
                                            {getBalanceLabel(
                                                period.total,
                                                company.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>

                        <TableFooter>
                            <TableRow>
                                <TableFooterCell>
                                    <SMBold>
                                        {t('total_for_month', {
                                            month: monthName
                                        })}
                                    </SMBold>
                                </TableFooterCell>
                                <TableFooterCell></TableFooterCell>
                                <TableFooterCell align="right">
                                    <SMBold>
                                        {getBalanceLabel(
                                            tipsReconciliation?.data
                                                ?.tipsReconciliationSummary
                                                ?.monthTotal,
                                            company.currency
                                        )}
                                    </SMBold>
                                </TableFooterCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </TableCard>

            <Box m={4}></Box>

            {/* Hourly employees - multiple periods */}
            {tipsReconciliation?.data?.hourlyEmployeesTipsReconciliation?.map(
                (period, index) => (
                    <div key={`hourly-table-${index}`}>
                        <TipsReconciliationTable
                            items={period.tipsTransactions || []}
                            label={t('hourly_employees')}
                            chipText={formatPayrollPeriod(
                                period.payrollPeriodStart,
                                period.payrollPeriodEnd
                            )}
                            totalAmount={getBalanceLabel(
                                period.total,
                                company.currency
                            )}
                        />
                        <Box m={4}></Box>
                    </div>
                )
            )}

            {/* Fulltime employees - multiple periods */}
            {tipsReconciliation?.data?.fullTimeEmployeesTipsReconciliation?.map(
                (period, index) => {
                    const fullTimeArray =
                        tipsReconciliation?.data
                            ?.fullTimeEmployeesTipsReconciliation;
                    const isLastItem = index === fullTimeArray.length - 1;

                    return (
                        <div key={`fulltime-table-${index}`}>
                            <TipsReconciliationTable
                                items={period.tipsTransactions || []}
                                totalAmount={getBalanceLabel(
                                    period.total,
                                    company.currency
                                )}
                                chipText={formatPayrollPeriod(
                                    period.payrollPeriodStart,
                                    period.payrollPeriodEnd
                                )}
                                label={t('fulltime_employees')}
                            />
                            {!isLastItem && <Box m={4}></Box>}
                        </div>
                    );
                }
            )}
        </div>
    );
};
